import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type AuditRow } from '../lib/api';

const ACTIONS = [
  { value: '', label: 'All actions' },
  { value: 'tenant.create', label: 'Tenant created' },
  { value: 'tenant.rename', label: 'Mailbox rename' },
  { value: 'tenant.resync', label: 'Resync from M365' },
  { value: 'tenant.domain_swap', label: 'Domain swap' },
];

function actorLabel(actor: string): string {
  if (actor.startsWith('user:')) return `user ${actor.slice(5, 13)}`;
  if (actor.startsWith('token:')) return `token ${actor.slice(6, 14)}`;
  if (actor === 'admin_cookie') return 'admin cookie';
  return actor;
}

function fmtDetail(d: Record<string, unknown>): string {
  const keys = Object.keys(d);
  if (!keys.length) return '';
  return keys
    .map(k => `${k}=${typeof d[k] === 'string' ? d[k] : JSON.stringify(d[k])}`)
    .join(' · ');
}

export default function AuditLog() {
  const [action, setAction] = useState('');
  const q = useQuery({
    queryKey: ['audit-log', action],
    queryFn: () => api.listAudit({ action: action || undefined, limit: 500 }),
    refetchInterval: 15_000,
  });

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg text-ink font-medium">Audit log</h1>
          <div className="text-xs text-dim mt-0.5">
            Workspace-scoped record of mutating operations. Refreshes every 15s.
          </div>
        </div>
        <select
          className="input w-56"
          value={action}
          onChange={e => setAction(e.target.value)}
        >
          {ACTIONS.map(a => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
        </select>
      </div>

      {q.isLoading && <div className="text-xs text-dim">Loading…</div>}
      {q.isError && (
        <div className="text-xs text-err">Failed to load audit log.</div>
      )}
      {q.data && q.data.length === 0 && (
        <div className="card p-6 text-sm text-dim">
          No audit rows yet for this filter.
        </div>
      )}
      {q.data && q.data.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-dim uppercase tracking-wider">
              <tr className="border-b border-line">
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Tenant</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Detail</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r: AuditRow) => (
                <tr key={r.id} className="border-b border-line/40">
                  <td className="px-3 py-2 whitespace-nowrap text-dim font-mono text-xs">
                    {new Date(r.ts).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-ink font-mono text-xs">
                    {r.action}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
                    {r.tenant_id ? (
                      <Link
                        to={`/tenants/${r.tenant_id}`}
                        className="text-accent hover:underline"
                      >
                        {r.tenant_id.slice(0, 8)}
                      </Link>
                    ) : (
                      <span className="text-dim">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-dim font-mono text-xs">
                    {actorLabel(r.actor)}
                  </td>
                  <td className="px-3 py-2 text-dim font-mono text-[11px]">
                    {fmtDetail(r.detail)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
